/**
 * Quais documentos o colaborador VÊ — regra única para o app inteiro.
 *
 * Duas exclusões:
 *  - documento INATIVO (`ativo_inativo = 'I'`): o administrativo o retirou;
 *  - documento da COMPETÊNCIA DE TESTE (12/2222): data fixa combinada em 07/10/2026 para
 *    testar a entrega de arquivos (importação, separação, assinatura do lote) com
 *    colaboradores reais sem que o documento chegue a eles.
 *
 * Toda superfície passa por aqui: o `applyLocalDocStatus` (services/Sync.tsx) tira esses
 * documentos do `userData` e o `documentoEstado` os marca como não visíveis (`visivel`),
 * para quem resolve a lista. Uma tela que lesse `userData.documentos` por fora dos dois
 * mostraria o documento de teste — e o modal da Home cobraria a assinatura dele.
 *
 * Sem imports de propósito: o Sync.tsx e o documentoEstado.ts usam isto, e o
 * documentoEstado já importa o Sync.
 */

/** Competência (mês/ano de referência) reservada para teste de entrega de documentos. */
export const COMPETENCIA_TESTE_DOCUMENTOS = { mes: 12, ano: 2222 } as const;

// Campos de data que um documento pode trazer. O `/iniciar` atual manda só
// `mes_ano_referencia` ("MM/AAAA"); os outros cobrem cache e backend antigos.
const CAMPOS_DATA = [
  "mes_ano_referencia",
  "data_competencia",
  "mes_ano_referencia_ordem",
  "ordem_data",
];

/** Mês e ano de uma data em "MM/AAAA", "DD/MM/AAAA" ou "AAAA-MM[-DD…]". */
const mesAnoDe = (valor: unknown): { mes: number; ano: number } | null => {
  const s = String(valor ?? "").trim();
  if (!s) return null;
  let m = s.match(/^(\d{1,2})\s*\/\s*(\d{4})$/);
  if (m) return { mes: Number(m[1]), ano: Number(m[2]) };
  m = s.match(/^\d{1,2}\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})/);
  if (m) return { mes: Number(m[1]), ano: Number(m[2]) };
  m = s.match(/^(\d{4})-(\d{1,2})(?:\D|$)/);
  if (m) return { mes: Number(m[2]), ano: Number(m[1]) };
  return null;
};

/** O documento é da competência de teste? Basta um campo de data dizer 12/2222. */
export const ehDocumentoDeTeste = (doc: any): boolean =>
  CAMPOS_DATA.some((campo) => {
    const d = mesAnoDe(doc?.[campo]);
    return (
      !!d &&
      d.mes === COMPETENCIA_TESTE_DOCUMENTOS.mes &&
      d.ano === COMPETENCIA_TESTE_DOCUMENTOS.ano
    );
  });

/** O colaborador pode ver este documento (listar, abrir, assinar, ser cobrado)? */
export const documentoVisivelAoColaborador = (doc: any): boolean =>
  String(doc?.ativo_inativo ?? "A")
    .trim()
    .toUpperCase() !== "I" && !ehDocumentoDeTeste(doc);
